/**
 * 参加船名簿 API（/api/vessels/*）。担当: G1
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { masterKeys, type TaxRounding } from '@/features/masters/api';

export interface VesselListItem {
  id: number;
  vesselNo: number;
  displayOrder: number;
  businessOperatorId: number;
  businessOperatorName: string;
  name: string;
  nameKana: string;
  ownerName: string;
  registrationNo: string;
  tonnage: number | null;
  approvalFrom: string | null;
  approvalTo: string | null;
  isApproved: boolean;
  captainName: string | null;
  totalBoardingCount: number | null;
}

export interface VesselPerson {
  id?: number;
  name: string;
  approvalFrom: string | null;
  approvalTo: string | null;
  note: string | null;
}

export interface VesselCapacity {
  id?: number;
  boardingCount: number;
  totalBoardingCount: number;
  approvalFrom: string | null;
  approvalTo: string | null;
  note: string | null;
}

export interface VesselDetail {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselNo: number;
  displayOrder: number;
  ownerName: string;
  name: string;
  nameKana: string;
  registrationNo: string;
  tonnage: number;
  horsepower: string | null;
  phone: string | null;
  phoneFrom: string | null;
  phoneTo: string | null;
  approvalFrom: string | null;
  approvalTo: string | null;
  isApproved: boolean;
  taxRounding: TaxRounding;
  fuelType: string | null;
  isInvoiceRegistered: boolean;
  billingAddressee: string | null;
  claimantName: string | null;
  claimantAddress: string | null;
  claimantInvoiceNo: string | null;
  charterFeeRecipient: string | null;
  address: string | null;
  isMutualAidTarget: boolean;
  planDaysQ1: number;
  planDaysQ2: number;
  planDaysQ3: number;
  planDaysQ4: number;
  note: string | null;
  captains: VesselPerson[];
  crewMembers: VesselPerson[];
  capacities: VesselCapacity[];
  fishingMethods: string[];
}

export interface VesselListFilter {
  businessOperatorId?: string;
  name?: string;
  registrationNo?: string;
  displayOrder?: string;
  approved?: '' | 'true' | 'false';
}

export interface VesselIssue {
  code?: string;
  path?: string;
  rowIndex?: number;
  message: string;
}

export const vesselKeys = {
  all: ['vessels'] as const,
  list: (year: number, f: VesselListFilter) => [...vesselKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...vesselKeys.all, 'detail', id] as const,
  bulk: (year: number) => [...vesselKeys.all, 'bulk-approval', year] as const,
  plan: (year: number, f: unknown) => [...vesselKeys.all, 'plan-days', year, f] as const,
};

export function useVessels(year: number, filter: VesselListFilter) {
  return useQuery({
    queryKey: vesselKeys.list(year, filter),
    queryFn: ({ signal }) =>
      api.get<{ items: VesselListItem[]; total: number }>('/vessels', { fiscalYear: year, ...filter, pageSize: 500, sort: 'displayOrder' }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useVessel(id: number | null) {
  return useQuery({
    queryKey: vesselKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<VesselDetail>(`/vessels/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

/** 業務入力用の選択肢（options）も無効化する */
function useInvalidate() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: vesselKeys.all }),
      qc.invalidateQueries({ queryKey: [...masterKeys.all, 'vessel-options'] }),
    ]);
}

export interface SaveResult {
  id: number;
  vesselNo: number;
  warnings: VesselIssue[];
}

export function useCreateVessel() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: unknown) => api.post<SaveResult>('/vessels', body),
    onSuccess: invalidate,
  });
}

export function useUpdateVessel(id: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: unknown) => api.put<SaveResult>(`/vessels/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteVessel() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/vessels/${id}`),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- 一括承認（S-12）
export interface BulkApprovalRow {
  businessOperatorId: number;
  name: string;
  displayOrder: number;
  bulkApprovalFrom: string | null;
  bulkApprovalTo: string | null;
  vesselCount: number;
  approvedCount: number;
}

export function useBulkApprovalList(year: number) {
  return useQuery({
    queryKey: vesselKeys.bulk(year),
    queryFn: ({ signal }) => api.get<{ items: BulkApprovalRow[] }>('/vessels/bulk-approval', { fiscalYear: year }, { signal }),
  });
}

export function useBulkApprove() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { fiscalYear: number; businessOperatorIds: number[]; approvalFrom: string; approvalTo: string }) =>
      api.post<{ updatedVessels: number; perOperator: { businessOperatorId: number; vessels: number }[] }>('/vessels/bulk-approval', body),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- 計画日数（S-13）
export interface PlanDaysRow {
  vesselId: number;
  vesselNo: number;
  displayOrder: number;
  businessOperatorId: number;
  businessOperatorName: string;
  name: string;
  registrationNo: string;
  isApproved: boolean;
  fishingMethods: string[];
  planDaysQ1: number;
  planDaysQ2: number;
  planDaysQ3: number;
  planDaysQ4: number;
  total: number;
}

export interface PlanDaysFilter {
  businessOperatorId?: string;
  name?: string;
  fishingMethod?: string;
}

export function usePlanDays(year: number, filter: PlanDaysFilter) {
  return useQuery({
    queryKey: vesselKeys.plan(year, filter),
    queryFn: ({ signal }) => api.get<{ items: PlanDaysRow[]; totalLimitDays: number | null }>('/vessels/plan-days', { fiscalYear: year, ...filter }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useSavePlanDays() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { fiscalYear: number; items: { vesselId: number; planDaysQ1: number; planDaysQ2: number; planDaysQ3: number; planDaysQ4: number }[] }) =>
      api.put<{ updated: number }>('/vessels/plan-days', body),
    onSuccess: invalidate,
  });
}
